import React, { useEffect, useRef, useState } from "react";
import { SharedButton } from "../../../shared";
import { cloudinaryThumb } from "../../Footprint/cloudinary";
import { getUploadSignature } from "../../../../lib/uploadSignature";
import { uploadToCloudinary } from "../../../../lib/cloudinaryUpload";

// The Cloudinary plan's own upload limits (the account's media_limits, read
// 2026-09-15, free plan): 10 MB for images and documents, 100 MB for video.
// A larger file is refused by Cloudinary after it has finished uploading, so
// checking here first saves a long upload that was never going to work. If the
// plan changes, change these to match.
const MB = 1024 * 1024;
const LIMITS = {
  video: { bytes: 100 * MB, label: "100 MB" },
  other: { bytes: 10 * MB, label: "10 MB" },
};

function limitFor(file) {
  return file.type.startsWith("video/") ? LIMITS.video : LIMITS.other;
}

const FILE_TYPE_BADGES = {
  pdf: { label: "PDF", bg: "#FEE2E2", color: "#991B1B" },
  doc: { label: "DOC", bg: "#DBEAFE", color: "#1E40AF" },
  docx: { label: "DOCX", bg: "#DBEAFE", color: "#1E40AF" },
  xls: { label: "XLS", bg: "#D1FAE5", color: "#065F46" },
  xlsx: { label: "XLSX", bg: "#D1FAE5", color: "#065F46" },
  txt: { label: "TXT", bg: "#F3F4F6", color: "#374151" },
  csv: { label: "CSV", bg: "#D1FAE5", color: "#065F46" },
};

function getResourceType(file) {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "raw";
}

function getExtension(filename) {
  return filename.split(".").pop().toLowerCase();
}

const DOC_EXTENSIONS = ["pdf", "doc", "docx", "xls", "xlsx", "txt", "csv", "zip", "ppt", "pptx"];

function getEffectiveType(attachment) {
  const ext = attachment.original_name?.split(".").pop()?.toLowerCase();
  if (DOC_EXTENSIONS.includes(ext)) return "raw";
  return attachment.resource_type;
}

// Cloudinary's refusals are written for developers ("File size too large. Got
// 12000000. Maximum is 10485760."). The person posting needs the reason, not the
// numbers; the full message still goes to the console.
function explainRefusal(message = "") {
  if (/too large|file size/i.test(message)) return "it’s too big";
  if (/invalid|unsupported|format|not allowed/i.test(message)) return "that kind of file isn’t accepted";
  return "please try again";
}

function FileUploader({ attachments, onAttachmentsChange, maxFiles = 10, onUploadingChange }) {
  const [isUploading, setIsUploading] = useState(false);
  const [previews, setPreviews] = useState([]);
  // Files that did not make it, and why. Before this, a refused or interrupted
  // upload simply disappeared from the form and the person was left to guess.
  const [problems, setProblems] = useState([]);
  const fileInputRef = useRef(null);

  // Lets a form hold its submit button while a file is still on its way, so a
  // post cannot go out without the photo that was still uploading.
  useEffect(() => {
    if (onUploadingChange) onUploadingChange(previews.length > 0);
  }, [previews.length, onUploadingChange]);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    setProblems([]);

    const remainingSlots = maxFiles - attachments.length - previews.length;
    const filesToUpload = files.slice(0, remainingSlots);
    const overflow = files.slice(Math.max(remainingSlots, 0)).map((file) => ({
      name: file.name,
      why: `only ${maxFiles} files fit in one post`,
    }));
    if (overflow.length > 0) setProblems((prev) => [...prev, ...overflow]);
    if (filesToUpload.length === 0) return;

    // Check file sizes
    const validFiles = filesToUpload.filter((file) => {
      const limit = limitFor(file);
      if (file.size > limit.bytes) {
        setProblems((prev) => [...prev, { name: file.name, why: `it’s over the ${limit.label} limit` }]);
        return false;
      }
      return true;
    });
    if (validFiles.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const newPreviews = validFiles.map((file) => ({
      localUrl: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null,
      file,
      uploading: true,
      resourceType: getResourceType(file),
      originalName: file.name,
    }));
    setPreviews((prev) => [...prev, ...newPreviews]);
    setIsUploading(true);

    // One signature from our server covers the whole selection (valid an hour).
    // If the server will not sign, nothing is sent to Cloudinary at all.
    let permission;
    try {
      permission = await getUploadSignature();
    } catch (error) {
      console.error("Upload permission refused:", error);
      setPreviews((prev) => prev.filter((p) => !newPreviews.includes(p)));
      newPreviews.forEach((p) => p.localUrl && URL.revokeObjectURL(p.localUrl));
      setProblems((prev) => [
        ...prev,
        // The server's own reason when it gave one (for example, uploading turned
        // off for this account), so nobody is told to "try again later" in vain.
        ...newPreviews.map((p) => ({
          name: p.originalName,
          why: (error && error.message ? error.message : "Uploads aren’t available right now").replace(/\.$/, ""),
        })),
      ]);
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    for (const preview of newPreviews) {
      try {
        // "auto" so Cloudinary works out whether it is an image, video or file
        const data = await uploadToCloudinary(preview.file, { permission });

        if (data.error) {
          console.error("Cloudinary error:", data.error.message);
          setPreviews((prev) =>
            prev.filter((p) => p.file !== preview.file)
          );
          if (preview.localUrl) URL.revokeObjectURL(preview.localUrl);
          setProblems((prev) => [
            ...prev,
            { name: preview.originalName, why: explainRefusal(data.error.message) },
          ]);
          continue;
        }

        // Remove preview and add to attachments
        setPreviews((prev) => prev.filter((p) => p.file !== preview.file));
        if (preview.localUrl) URL.revokeObjectURL(preview.localUrl);

        const attachment = {
          url: data.secure_url,
          original_name: preview.originalName,
          resource_type: data.resource_type || preview.resourceType,
          // Cloudinary's own handle for the file, which is what deleting it takes.
          // It can usually be worked back out of the URL, but not reliably (a
          // document keeps its extension in the id; a name with dots is
          // ambiguous), so it is kept as given.
          public_id: data.public_id,
        };
        onAttachmentsChange((current) => [...current, attachment]);
      } catch (error) {
        console.error("Upload failed:", error);
        setPreviews((prev) => prev.filter((p) => p.file !== preview.file));
        if (preview.localUrl) URL.revokeObjectURL(preview.localUrl);
        setProblems((prev) => [
          ...prev,
          { name: preview.originalName, why: "the connection dropped — please try again" },
        ]);
      }
    }

    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveAttachment = (index) => {
    onAttachmentsChange(attachments.filter((_, i) => i !== index));
  };

  const handleRemovePreview = (file) => {
    setPreviews((prev) => {
      const target = prev.find((p) => p.file === file);
      if (target?.localUrl) URL.revokeObjectURL(target.localUrl);
      return prev.filter((p) => p.file !== file);
    });
  };

  const totalCount = attachments.length + previews.length;

  const renderAttachment = (att, index) => {
    const ext = getExtension(att.original_name);
    const effectiveType = getEffectiveType(att);
    const isImage = effectiveType === "image";
    const isVideo = effectiveType === "video";
    const badge = FILE_TYPE_BADGES[ext] || {
      label: ext.toUpperCase(),
      bg: "#F3F4F6",
      color: "#374151",
    };

    return (
      <div key={`att-${index}`} className="relative group">
        {isImage ? (
          <img
            src={cloudinaryThumb(att.url)}
            alt={att.original_name}
            className="w-[120px] h-[120px] object-cover rounded-[12px] border-2 border-dupontGold shadow-md"
          />
        ) : isVideo ? (
          <div className="w-[120px] h-[120px] rounded-[12px] border-2 border-dupontGold shadow-md bg-gray-900 flex flex-col items-center justify-center gap-y-[4px] overflow-hidden">
            <div className="text-[28px]">&#9654;</div>
            <div className="text-[12px] text-gray-300 px-[4px] text-center truncate w-full">
              {att.original_name}
            </div>
          </div>
        ) : (
          <div className="w-[120px] h-[120px] rounded-[12px] border-2 border-dupontGold shadow-md bg-white flex flex-col items-center justify-center gap-y-[6px] overflow-hidden">
            <span
              className="text-[11px] font-bold px-[8px] py-[2px] rounded-full"
              style={{ backgroundColor: badge.bg, color: badge.color }}
            >
              {badge.label}
            </span>
            <div className="text-[12px] text-dupontGray px-[6px] text-center truncate w-full">
              {att.original_name}
            </div>
          </div>
        )}
        {/* Remove button. type="button" matters: inside a <form> (The Scrapbook's)
            a bare <button> submits, and the × posted the photo it was meant to remove. */}
        <button
          type="button"
          onClick={() => handleRemoveAttachment(index)}
          className="absolute -top-[8px] -right-[8px] bg-red-500 hover:bg-red-600 text-white rounded-full w-[24px] h-[24px] text-[13px] font-bold flex items-center justify-center cursor-pointer shadow-md transition-colors"
        >
          &times;
        </button>
      </div>
    );
  };

  const renderPreview = (preview) => {
    return (
      <div key={preview.originalName} className="relative">
        {preview.localUrl ? (
          <img
            src={preview.localUrl}
            alt="Uploading..."
            className="w-[120px] h-[120px] object-cover rounded-[12px] border-2 border-dupontGold shadow-md opacity-70"
          />
        ) : (
          <div className="w-[120px] h-[120px] rounded-[12px] border-2 border-dupontGold shadow-md bg-gray-100 flex items-center justify-center opacity-70">
            <div className="text-[12px] text-dupontGray px-[6px] text-center truncate w-full">
              {preview.originalName}
            </div>
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center rounded-[12px] bg-black/30">
          <div className="w-[28px] h-[28px] border-[3px] border-white border-t-transparent rounded-full animate-spin" />
        </div>
        <button
          type="button"
          onClick={() => handleRemovePreview(preview.file)}
          className="absolute -top-[8px] -right-[8px] bg-red-500 hover:bg-red-600 text-white rounded-full w-[24px] h-[24px] text-[13px] font-bold flex items-center justify-center cursor-pointer shadow-md transition-colors"
        >
          &times;
        </button>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-y-[12px]">
      <div className="text-[14px] text-dupontGray">
        Attachments ({totalCount}/{maxFiles})
      </div>

      {(attachments.length > 0 || previews.length > 0) && (
        <div className="flex flex-row flex-wrap gap-[10px]">
          {attachments.map((att, index) => renderAttachment(att, index))}
          {previews.map((preview) => renderPreview(preview))}
        </div>
      )}

      {problems.length > 0 && (
        <div role="alert" className="flex flex-col gap-y-[2px] text-[13px] text-red-600">
          {problems.map((p, i) => (
            <p key={`${p.name}-${i}`}>
              “{p.name}” wasn’t added — {p.why}.
            </p>
          ))}
        </div>
      )}

      {totalCount < maxFiles && (
        <div>
          <input
            type="file"
            multiple
            onChange={handleFileSelect}
            ref={fileInputRef}
            className="hidden"
          />
          <SharedButton
            disabled={isUploading}
            loadingText={isUploading ? "Uploading..." : undefined}
            color="secondary"
            onClick={() => fileInputRef.current?.click()}
          >
            Add Files
          </SharedButton>
        </div>
      )}
    </div>
  );
}

export default FileUploader;
